import Invoice from "@/types/Invoice.interface"

interface State {
    invoices: Invoice[]
    newInvoice: Invoice
}

const defaults = () => {
    return  {
        invoices: [],
        newInvoice: {
            number: null,
            person_id: null,
            balance: null,
            total: null,
            due_date: new Date().toISOString().substr(0, 10),
            lines: [
                {
                    Description: null,
                    Quantity: 1,
                    Price: parseFloat('0').toFixed(2),
                    Tax: parseFloat('0').toFixed(2)
                }
            ]
        }
    }
}

export default {
    state: defaults(),
    mutations: {
        invoicesPush (state: State, invoices: Invoice[]): void {
            state.invoices = window.removeDuplicatesById(state.invoices, invoices)
        },
        invoicesRemove (state: State, ids: number[]): void {
            //  Get our global list of invoices
            const invoices = state.invoices

            //  If no invoices, do nothing
            if (!invoices) return

            //  Else, filter out our removed ids
            const filtered = invoices.filter((x: Invoice) => !ids.includes(x.id))

            //  Replace our list with the filtered list
            state.invoices = filtered
        },
        invoicesReset (state: State): void {
            Object.assign(state, defaults())
        },
        invoicesSet (state: State, invoices: Invoice[]): void {
            state.invoices = invoices
        }
    },
    actions: {
        invoicesFind ({ getters }, invoiceId: number): Promise<Invoice> {
            //  Assemble the API endpoint
            const url = `${getters.api}/invoices/${invoiceId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our found invoice
                return response.data
            }).catch(() => null)
        },
        invoicesPost ({ commit, getters }, invoice: Invoice): Promise<Invoice> {
            //  Assemble the API endpoint
            const url = `${getters.api}/invoices`

            //  Send the request
            return window.axios.post(url, invoice).then(response => {
                //  Update the invoice in our list
                commit('invoicesPush', [response.data])

                //  Update the invoice in our payments module
                commit('paymentsInvoicesPush', [response.data])

                //  Return our newly created invoice
                return response.data
            }).catch(() => null)
        },
        invoicesPut ({ commit, getters }, invoice: Invoice): Promise<Invoice> {
            //  Assemble the API endpoint
            const url = `${getters.api}/invoices`

            //  Send the request
            return window.axios.put(url, invoice).then(response => {
                //  Update the invoice in our list
                commit('invoicesPush', [response.data])

                //  Update the invoice in our payments module
                commit('paymentsInvoicesPush', [response.data])

                //  Return our updated invoice
                return response.data
            }).catch(() => null)
        },
        invoicesSearch ({ getters }, params): Promise<Invoice[]> {
            //  If we were not supplied params OR the searchtext on our params are empty, do nothing
            if (!params || !params.searchtext) return null

            //  Else, construct our API endpoint
            const url = `${getters.api}/invoices/search?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return our search results
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        //  Determines if the tenant has an invoice data provider
        hasInvoices (_, getters): boolean {
            //  Get the current tenant
            const tenant = getters.tenant

            //  If a tenant does NOT exist, return false
            if (!tenant) return false

            //  If tenant is connected to Sphere, enable invoices
            if (getters.isSphere) return true

            //  Else, get the tenant's data provider
            const dataProvider = tenant.data_provider

            //  If the data provider does NOT exist, return false
            if (!dataProvider) return false

            //  Else, check if the tenant has invoices (ie is QBO or Xero)
            const hasInvoices = dataProvider === 'QBO' || dataProvider === 'XERO'

            //  Return the result
            return hasInvoices
        },
        //  Our invoices dataset
        invoices (state: State): Invoice[] {
            return state.invoices
        },
        newInvoice(state: State): Invoice {
            return state.newInvoice
        }
    }
}